import { fetchMenus } from '@/api/menu'
import router, { asyncRouteRegistry, buildLayoutRoute, resetRouter } from '@/router'
import { getMenus, setMenus, removeMenus } from '@/utils/auth'

const fallbackMenus = [
  {
    id: 'dashboard',
    name: '工作台',
    path: '/dashboard',
    icon: 'el-icon-data-board',
    children: []
  }
]

const state = {
  menus: getMenus(),
  routesReady: false
}

const mutations = {
  SET_MENUS(state, menus) {
    state.menus = menus
  },
  SET_ROUTES_READY(state, ready) {
    state.routesReady = ready
  }
}

const actions = {
  async loadMenus({ commit, dispatch }) {
    const remoteMenus = await fetchMenus()
    const menus = normalizeMenus(remoteMenus)
    setMenus(menus)
    commit('SET_MENUS', menus)
    await dispatch('applyRoutes', menus)
    return menus
  },
  async restoreRoutes({ dispatch }) {
    // Menu permissions can change while a browser tab is closed.  A refresh
    // must recover the current server-side menu rather than re-authorising
    // routes from the persisted localStorage snapshot.
    await dispatch('loadMenus')
  },
  applyRoutes({ commit }, menus) {
    resetRouter()
    const allowedRoutes = filterRoutesByMenus(menus)
    router.addRoutes([buildLayoutRoute(allowedRoutes), { path: '*', redirect: '/dashboard' }])
    commit('SET_ROUTES_READY', true)
  },
  clear({ commit }) {
    removeMenus()
    commit('SET_MENUS', [])
    commit('SET_ROUTES_READY', false)
    resetRouter()
  }
}

function normalizeMenus(menus) {
  const remoteMenus = Array.isArray(menus) ? menus : []
  const normalized = remoteMenus.map(normalizeMenu).filter(Boolean)
  const result = normalized.length > 0 ? normalized : fallbackMenus

  if (!result.some(menu => menu.path === '/dashboard')) {
    result.unshift(fallbackMenus[0])
  }

  return dedupeMenus(result)
}

function normalizeMenu(menu) {
  if (!menu) {
    return null
  }
  const rawPath = rawMenuPath(menu)
  // A label is not a page address. Empty URLs are directories, including
  // custom names such as “UAT0907临时模块”; they must not claim system routes.
  const normalizedPath = rawPath ? normalizePath(rawPath) : ''
  const legacyMenu = !asyncRouteRegistry[normalizedPath] && isLegacyMenuPath(rawPath)
  const path = legacyMenu ? legacyMenuPath(menu, rawPath) : normalizedPath
  const legacyKey = legacyMenu ? legacyMenuKey(menu, rawPath) : ''
  const children = Array.isArray(menu.children)
    ? dedupeMenus(menu.children.map(normalizeMenu).filter(Boolean))
    : []

  if (rawPath && !routeExists(path) && children.length === 0) {
    debugMenuNormalize(menu, path)
    return null
  }

  return {
    id: menu.id || menu.moduleId || path,
    name: menu.name || routeTitle(path),
    path,
    icon: menu.icon || routeIcon(path),
    permissionType: menu.permissionType,
    actionIds: Array.isArray(menu.actionIds) ? menu.actionIds.map(String) : [],
    originalPath: rawPath,
    legacyPath: legacyMenu || isLegacyMenuPath(rawPath) ? rawPath : '',
    legacyMenuKey: legacyKey,
    migrationStatus: legacyMenu ? '该旧系统功能已识别并保留菜单入口，完整 Vue 复刻纳入 P1 / 后续迁移。' : '',
    children
  }
}

function normalizePath(value) {
  const raw = String(value || '').toLowerCase()
  if (asyncRouteRegistry[raw]) return raw
  if (raw.indexOf('modulelist.jsp') >= 0 || raw.indexOf('模块管理') >= 0) {
    return '/system/modules'
  }
  if (raw.indexOf('rolelist.jsp') >= 0 || raw.indexOf('角色管理') >= 0) {
    return '/system/roles'
  }
  if (raw.indexOf('approval') >= 0 || raw.indexOf('审批') >= 0) {
    return '/approval'
  }
  if (raw.indexOf('deliverable') >= 0 || raw.indexOf('achievement') >= 0 || raw.indexOf('achievements') >= 0 || raw.indexOf('成果') >= 0) {
    return '/deliverables'
  }
  if (raw.indexOf('share') >= 0 || raw.indexOf('focus') >= 0 || raw.indexOf('焦点') >= 0 || raw.indexOf('关注') >= 0) {
    return '/shares'
  }
  if (raw.indexOf('evaluation') >= 0 || raw.indexOf('projectevaluate') >= 0 || raw.indexOf('项目评估') >= 0) {
    return '/project-evaluations'
  }
  if (raw.indexOf('performance') >= 0 || raw.indexOf('绩效') >= 0 || raw.indexOf('评估') >= 0) {
    return '/performance'
  }
  if (raw.indexOf('serviceitem') >= 0 || raw.indexOf('service-item') >= 0 || raw.indexOf('服务事项') >= 0) {
    return '/service-items'
  }
  if (raw.indexOf('processitem') >= 0 || raw.indexOf('process-item') >= 0 || raw.indexOf('过程文件') >= 0) {
    return '/report-workbench'
  }
  if (raw.indexOf('template') >= 0 || raw.indexOf('模板') >= 0) {
    return '/templates'
  }
  if (raw.indexOf('document') >= 0 || raw.indexOf('file') >= 0 || raw.indexOf('文档') >= 0 || raw.indexOf('资料') >= 0) {
    return '/documents'
  }
  if (raw.indexOf('media') >= 0 || raw.indexOf('upload') >= 0 || raw.indexOf('媒体') >= 0 || raw.indexOf('上传') >= 0) {
    return '/media'
  }
  if (raw.indexOf('stat') >= 0 || raw.indexOf('统计') >= 0) {
    return '/statistics'
  }
  if (raw.indexOf('camera') >= 0 || raw.indexOf('摄像') >= 0) {
    return '/cameras'
  }
  if (raw.indexOf('room') >= 0 || raw.indexOf('房间') >= 0 || raw.indexOf('录像室') >= 0) {
    return '/rooms'
  }
  if (raw.indexOf('video') >= 0 || raw.indexOf('视频') >= 0 || raw.indexOf('录像') >= 0) {
    return '/videos'
  }
  if (raw.indexOf('interview') >= 0 || raw.indexOf('现场') >= 0 || raw.indexOf('走访') >= 0 || raw.indexOf('访谈') >= 0) {
    return '/interviews'
  }
  if (raw.indexOf('department') >= 0 || raw.indexOf('部门') >= 0) {
    return '/departments'
  }
  if (raw.indexOf('company') >= 0 || raw.indexOf('机构') >= 0 || raw.indexOf('公司') >= 0) {
    return '/companies'
  }
  if (raw.indexOf('user') >= 0 || raw.indexOf('用户') >= 0) {
    return '/users'
  }
  if (raw.indexOf('person') >= 0 || raw.indexOf('服务对象') >= 0) {
    return '/persons'
  }
  if (raw.indexOf('authorize') >= 0 || raw.indexOf('permission') >= 0 || raw.indexOf('role') >= 0 || raw.indexOf('权限') >= 0 || raw.indexOf('角色') >= 0) {
    return '/system/roles'
  }
  if (raw.indexOf('module') >= 0 || raw.indexOf('模块') >= 0) {
    return '/system/modules'
  }
  if (raw.indexOf('project') >= 0 || raw.indexOf('项目') >= 0) {
    return '/project/list'
  }
  if (raw.indexOf('dashboard') >= 0 || raw.indexOf('home') >= 0 || raw.indexOf('工作台') >= 0) {
    return '/dashboard'
  }
  if (raw === '/project') {
    return '/project/list'
  }
  if (raw.charAt(0) === '/') {
    return raw
  }
  return `/${raw}`
}

function rawMenuPath(menu) {
  return String((menu && (menu.path || menu.url)) || '').trim()
}

function isLegacyMenuPath(path) {
  const value = String(path || '').toLowerCase()
  return value.indexOf('/view/') >= 0 ||
    value.indexOf('.jsp') >= 0 ||
    value.indexOf('.action') >= 0 ||
    value.indexOf('/index.jsp') >= 0
}

function legacyMenuPath(menu, rawPath) {
  return `/legacy-menu/${legacyMenuKey(menu, rawPath)}`
}

function legacyMenuKey(menu, rawPath) {
  const preferred = String((menu && (menu.id || menu.moduleId)) || '').trim()
  if (preferred) {
    return safeSlug(preferred)
  }
  const source = rawPath || (menu && menu.name) || 'legacy-menu'
  return `legacy-${hashCode(source)}`
}

function safeSlug(value) {
  const slug = String(value || '').trim().replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '')
  return slug || 'legacy-menu'
}

function hashCode(value) {
  const text = String(value || '')
  let hash = 0
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(index)
    hash |= 0
  }
  return Math.abs(hash)
}

function routeExists(path) {
  return !!asyncRouteRegistry[path] || isLegacyMenuRoute(path)
}

function isLegacyMenuRoute(path) {
  return String(path || '').indexOf('/legacy-menu/') === 0
}

function filterRoutesByMenus(menus) {
  const routePaths = flattenMenuPaths(menus)
  if (!routePaths.includes('/dashboard')) {
    routePaths.unshift('/dashboard')
  }
  return routePaths
    .map(routeRegistryKey)
    .filter((path, index, list) => list.indexOf(path) === index)
    .map(path => asyncRouteRegistry[path])
    .filter(Boolean)
}

function routeRegistryKey(path) {
  return isLegacyMenuRoute(path) ? '/legacy-menu' : path
}

function flattenMenuPaths(menus) {
  const paths = []
  menus.forEach(menu => {
    if (menu.path && routeExists(menu.path)) {
      paths.push(menu.path)
    }
    if (menu.children && menu.children.length) {
      paths.push(...flattenMenuPaths(menu.children))
    }
  })
  return paths
}

function dedupeMenus(menus) {
  const seen = new Set()
  return menus.filter(menu => {
    if (!menu) {
      return false
    }
    // Distinct modules may share an explicit URL. Do not lose a module's
    // authorised actions or children merely because another uses that page.
    const key = menu.id ? `id:${menu.id}` : `path:${menu.path}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

function routeTitle(path) {
  const route = asyncRouteRegistry[path] || (isLegacyMenuRoute(path) && asyncRouteRegistry['/legacy-menu'])
  return route && route.meta.title
}

function routeIcon(path) {
  const route = asyncRouteRegistry[path] || (isLegacyMenuRoute(path) && asyncRouteRegistry['/legacy-menu'])
  return route && route.meta.icon
}

function debugMenuNormalize(menu, path) {
  if (process.env.NODE_ENV === 'development' && window.localStorage && window.localStorage.DEBUG_MENU_NORMALIZE === '1') {
    // eslint-disable-next-line no-console
    console.warn('[menu-normalize] dropped menu without route', {
      id: menu && menu.id,
      name: menu && menu.name,
      path: menu && (menu.path || menu.url),
      normalizedPath: path,
      permissionType: menu && menu.permissionType
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
